import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, Loader2, RefreshCw, Search, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { R, fmtQty, pct, StatTile, ExplainerBox, EmptyState, ErrorState, errMsg } from "./shared";

const STATUS_META = {
  full: { label: "Fully costed", tone: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  partial: { label: "Partially costed", tone: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  direct: { label: "Direct cost", tone: "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30" },
  uncosted: { label: "Uncosted", tone: "bg-destructive/15 text-destructive border-destructive/30" },
};

/**
 * Session 38 — Inventory → Cost Coverage.
 * Answers "can I trust my profit numbers?" by showing, for every menu item,
 * whether its cost comes from a complete recipe, a partial recipe, a typed
 * direct cost, or nothing at all — and EXACTLY what is missing.
 */
export const CostCoveragePanel = ({ http, authHeaders, onOpenRecipes, onOpenIngredients }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await http.get("/owner/costing/coverage", { headers: authHeaders });
      setData(r.data);
    } catch (e) {
      setError(errMsg(e, "Unable to load cost coverage."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders]);

  useEffect(() => { load(); }, [load]);

  const items = useMemo(() => {
    const list = data?.items || [];
    const q = query.trim().toLowerCase();
    return list.filter((i) => (status === "all" || i.status === status) && (!q || String(i.name || "").toLowerCase().includes(q)));
  }, [data, status, query]);

  const s = data?.summary || {};
  const counts = { all: s.total || 0, full: s.fully_costed || 0, partial: s.partially_costed || 0, direct: s.direct_costed || 0, uncosted: s.uncosted || 0 };
  const missingCostIngredients = Array.isArray(s.ingredients_without_cost) ? s.ingredients_without_cost : [];

  return (
    <div className="space-y-3" data-testid="coverage-panel">
      <ExplainerBox title="What is this tab?" testId="coverage-explainer">
        <p>Profit reports can only count the cost of items that <em>have</em> a cost. This tab shows how much of your menu is costed and, for anything that isn't, the exact ingredient or recipe line to fix.</p>
        {s.explanation ? <p className="text-xs">{s.explanation}</p> : null}
      </ExplainerBox>

      {error ? <ErrorState message={error} onRetry={load} testId="coverage-error" /> : null}

      {loading && !data ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : data ? (
        <>
          <Card className="rounded-2xl border-border bg-card" data-testid="coverage-summary-card">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Menu cost coverage</p>
                  <p className="mt-1 text-3xl font-semibold tabular-nums" data-testid="coverage-pct">{pct(s.coverage_pct)}</p>
                  <p className="text-xs text-muted-foreground">{(s.fully_costed || 0) + (s.direct_costed || 0)} of {s.total || 0} menu items have a usable cost · {s.uncosted || 0} excluded from COGS</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {onOpenRecipes ? <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={onOpenRecipes} data-testid="coverage-open-recipes"><Wrench className="mr-1 h-4 w-4" />Edit recipes</Button> : null}
                  {onOpenIngredients ? <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={onOpenIngredients} data-testid="coverage-open-ingredients">Ingredient costs</Button> : null}
                  <Button type="button" size="sm" variant="outline" className="rounded-xl" onClick={load} disabled={loading} data-testid="coverage-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}</Button>
                </div>
              </div>
              <Progress value={Number(s.coverage_pct) || 0} className="mt-3 h-2" />
            </CardContent>
          </Card>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile label="Fully costed" value={counts.full} hint="Complete recipe, every ingredient costed" tone="good" testId="coverage-tile-full" />
            <StatTile label="Partially costed" value={counts.partial} hint="Recipe missing a cost or conversion" tone={counts.partial ? "warn" : "muted"} testId="coverage-tile-partial" />
            <StatTile label="Direct cost" value={counts.direct} hint="No recipe — a cost was typed on the item" tone="muted" testId="coverage-tile-direct" />
            <StatTile label="Uncosted" value={counts.uncosted} hint="Excluded from COGS until fixed" tone={counts.uncosted ? "bad" : "good"} testId="coverage-tile-uncosted" />
          </div>
          {missingCostIngredients.length ? (
            <Card className="rounded-2xl border-amber-500/40 bg-amber-500/10" data-testid="coverage-missing-ingredients">
              <CardContent className="flex flex-wrap items-center gap-2 p-3">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <p className="text-xs font-medium">{missingCostIngredients.length} ingredient{missingCostIngredients.length === 1 ? "" : "s"} without a cost:</p>
                {missingCostIngredients.slice(0, 10).map((n) => <Badge key={n} variant="outline" className="rounded-full">{n}</Badge>)}
                {missingCostIngredients.length > 10 ? <Badge variant="secondary" className="rounded-full">+{missingCostIngredients.length - 10}</Badge> : null}
              </CardContent>
            </Card>
          ) : null}
        </>
      ) : null}

      <Card className="rounded-2xl border-border bg-card" data-testid="coverage-items-card">
        <CardContent className="p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Tabs value={status} onValueChange={setStatus} className="max-w-full">
              <TabsList className="h-auto max-w-full flex-wrap justify-start gap-0.5 rounded-xl">
                {["all", "uncosted", "partial", "direct", "full"].map((k) => (
                  <TabsTrigger key={k} value={k} className="rounded-lg text-xs" data-testid={`coverage-filter-${k}`}>{k === "all" ? "All" : STATUS_META[k].label} <span className="ml-1 tabular-nums text-muted-foreground">{counts[k]}</span></TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:ml-auto sm:w-48">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="search" placeholder="Find menu item…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-9 w-full rounded-xl pl-8" data-testid="coverage-search" />
            </div>
          </div>
          {loading && !data ? <Skeleton className="h-40 w-full rounded-2xl" /> : items.length ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8"></TableHead>
                    <TableHead>Menu item</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                    <TableHead className="hidden md:table-cell">What's missing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => {
                    const meta = STATUS_META[it.status] || STATUS_META.uncosted;
                    const expanded = !!open[it.id];
                    const hasDetail = (it.lines?.length || 0) + (it.missing?.length || 0) + (it.warnings?.length || 0) > 0;
                    const marginTone = it.margin_pct === null || it.margin_pct === undefined ? "" : it.margin_pct < 0 ? "text-destructive font-semibold" : it.margin_pct < 30 ? "text-amber-700 dark:text-amber-400 font-semibold" : "text-emerald-700 dark:text-emerald-400 font-semibold";
                    return (
                      <React.Fragment key={it.id}>
                        <TableRow className={hasDetail ? "cursor-pointer" : ""} onClick={() => hasDetail && setOpen((o) => ({ ...o, [it.id]: !o[it.id] }))} data-testid={`coverage-row-${it.id}`}>
                          <TableCell className="px-2">{hasDetail ? (expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />) : null}</TableCell>
                          <TableCell className="font-medium">{it.name}</TableCell>
                          <TableCell><Badge variant="outline" className={`rounded-full ${meta.tone}`} data-testid={`coverage-status-${it.id}`}>{meta.label}</Badge></TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{R(it.price)}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{it.cost !== null && it.cost !== undefined ? R(it.cost) : it.known_part_cost !== null && it.known_part_cost !== undefined ? <span className="text-muted-foreground" title="Only the costed part of the recipe">≥ {R(it.known_part_cost)}</span> : <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell className={`text-right text-xs tabular-nums ${marginTone}`}>{pct(it.margin_pct)}</TableCell>
                          <TableCell className="hidden text-xs text-muted-foreground md:table-cell">
                            {it.missing?.length ? it.missing.slice(0, 2).map((m, i) => <span key={i} className="block truncate">{m.hint || `${m.ingredient}: ${m.problem}`}</span>) : it.status === "uncosted" ? "Add a recipe or type a cost on the item" : it.status === "direct" ? "Add a recipe to track stock automatically" : "—"}
                            {it.missing?.length > 2 ? <span className="block">+{it.missing.length - 2} more</span> : null}
                          </TableCell>
                        </TableRow>
                        {expanded ? (
                          <TableRow className="bg-secondary/30 hover:bg-secondary/30" data-testid={`coverage-detail-${it.id}`}>
                            <TableCell colSpan={7} className="p-3">
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Recipe lines</p>
                                  {it.lines?.length ? (
                                    <ul className="mt-1 space-y-1 text-xs">
                                      {it.lines.map((l, i) => (
                                        <li key={i} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-2 py-1">
                                          <span>{l.ingredient || "Deleted ingredient"} · {fmtQty(l.quantity)} {l.unit}{l.quantity_in_stock_unit !== null && l.quantity_in_stock_unit !== undefined && l.unit !== l.stock_unit ? ` (= ${fmtQty(l.quantity_in_stock_unit)} ${l.stock_unit})` : ""}</span>
                                          <span className="tabular-nums">{l.line_cost !== null && l.line_cost !== undefined ? R(l.line_cost) : <span className="text-destructive">no cost</span>}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  ) : <p className="mt-1 text-xs text-muted-foreground">No recipe on this item.</p>}
                                </div>
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">To fix</p>
                                  {it.missing?.length || it.warnings?.length ? (
                                    <ul className="mt-1 space-y-1 text-xs">
                                      {(it.missing || []).map((m, i) => <li key={`m-${i}`} className="flex items-start gap-1.5"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" /><span>{m.hint || `${m.ingredient}: ${m.problem}`}</span></li>)}
                                      {(it.warnings || []).map((w, i) => <li key={`w-${i}`} className="text-muted-foreground">{typeof w === "string" ? w : w.hint || JSON.stringify(w)}</li>)}
                                    </ul>
                                  ) : <p className="mt-1 text-xs text-muted-foreground">Nothing — this item is fully costed.</p>}
                                  {onOpenRecipes ? <Button type="button" size="sm" variant="outline" className="mt-2 rounded-xl" onClick={(e) => { e.stopPropagation(); onOpenRecipes(it); }} data-testid={`coverage-fix-${it.id}`}><Wrench className="mr-1 h-3.5 w-3.5" />Open recipes</Button> : null}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : data ? <EmptyState title="No menu items match" body={status === "all" ? "Add menu items first." : `Nothing is ${STATUS_META[status]?.label?.toLowerCase() || status}.`} testId="coverage-empty" /> : null}
        </CardContent>
      </Card>
    </div>
  );
};
